import { Button } from 'components/deprecated/Button';
import { useCallback, useMemo, useState } from 'react';
import { isUrl, getExampleURLs } from 'utils/url';
import { cx } from '@linaria/core';
import { uniq } from 'rambda';
import { pipe } from 'utils/fp';
import { Alert } from 'components/Alert';
import { IconInfoFill } from 'components/Icons';
import { baseTheme, flex } from 'styles/utils';
import { cssHelpers } from 'styles/utils/core';
import { IModelPredictionStoreMST } from 'modules/Predictions/mst/ModelPredictionStore';
import { IWorkflowPredictionStoreMST } from 'modules/Predictions/mst/WorkflowPredictionStore';
import { Form, Formik } from 'formik';
import { InputField } from 'components/Formik/InputField';
import { ButtonGroup } from 'components/deprecated/Button/ButtonGroup';
import { updateExampleInputsTE as updateModelExampleInputs } from 'api/models/updateExampleInputs';
import { updateExampleInputsTE as updateWorkflowExampleInputs } from 'api/workflows/updateExampleInputs';
import { task, taskEither } from 'fp-ts';
import cogoToast from 'components/ToastMessage/ToastMessage';
import { useQueryClient } from 'react-query';
import { clearPredictionsTE } from 'api/models/clearPredictions';
import { contentWarning, exampleUrls, preserUrlInstructions, presetUrlTextArea, uploaderWrapper } from './styles';
export interface PresetInputsUploaderProps {
  editableEntity: CF.UI.EditableEntity;
  closeModal: () => void;
  inputType: CF.API.InputType;
  store: IModelPredictionStoreMST | IWorkflowPredictionStoreMST;
}
export function PresetInputsUploader({
  editableEntity,
  closeModal,
  inputType,
  store
}: PresetInputsUploaderProps): import('react').ReactElement {
  const queryClient = useQueryClient();
  const entityIsWorkflow = ('nodes' in editableEntity.data);
  const [isLoading, setIsloading] = useState(false);
  const defaultValues = useMemo(() => {
    const urls = ((editableEntity.data.metadata as CF.API.Metadata).presetInputs || []).slice(0, 5).map((i: CF.API.PresetInput) => i.url).join(', ');
    return {
      urls
    };
  }, [editableEntity.data]);
  const exampleURLs = useMemo(() => getExampleURLs(inputType), [inputType]);
  const submitHandler = useCallback(async ({
    urls
  }: {
    urls: string;
  }) => {
    const validUrls = filterValidUniqueUrls(urls);
    setIsloading(true);
    if (!validUrls || !validUrls.length) {
      clearPredictionsTE({
        body: {
          model: (editableEntity.data as CF.API.Models.Model)
        }
      })();
      await editableEntity.setValue('metadata', {
        ...editableEntity.data.metadata,
        presetInputs: []
      }, true);
      store.media.setPresetContent([]);
    } else {
      const newMetadata = {
        ...editableEntity.data.metadata,
        presetInputs: [...validUrls].slice(0, 5).map(x => ({
          url: x,
          type: inputType
        }))
      };
      const oldMetadata = {
        ...editableEntity.data.metadata
      };
      await editableEntity.setValue('metadata', newMetadata, false // don't immediately hit the API
      );
      const updateInputsTE = entityIsWorkflow ? updateWorkflowExampleInputs({
        body: {
          workflow: ({
            ...editableEntity.data,
            metadata: newMetadata
          } as CF.API.Workflows.Workflow)
        }
      }) : updateModelExampleInputs({
        body: {
          model: ({
            ...editableEntity.data,
            metadata: newMetadata
          } as CF.API.Models.Model)
        }
      });
      await pipe((updateInputsTE as taskEither.TaskEither<Error, CF.API.Models.ListModelsResponse | CF.API.Workflows.ListWorkflowsRes>), taskEither.fold(() => {
        cogoToast.error(`Could not update ${entityIsWorkflow ? 'workflow' : 'model'}.`);
        // reset metadata because optimistic update failed.
        editableEntity.setValue('metadata', oldMetadata, true);
        return task.of(null);
      }, () => {
        cogoToast.success(`${entityIsWorkflow ? 'Workflow' : 'Model'} updated.`);
        // update the store to reflect new preview inputs
        store.media.setPresetContentFromMetadata(newMetadata);
        return task.of(null);
      }))();
    }
    if (entityIsWorkflow) {
      queryClient.invalidateQueries(['Workflows']);
    } else {
      queryClient.invalidateQueries(['Models']);
    }
    closeModal();
    setIsloading(false);
    return true;
  }, []);
  const validate = ({
    urls
  }: {
    urls: string;
  }): {
    urls?: string;
  } => {
    const errors: {
      urls?: string;
    } = {};
    const validUrls = filterValidUniqueUrls(urls);
    if (urls && validUrls.length === 0) {
      errors.urls = 'Please ensure to have at least 1 valid URL that starts with http(s).';
    }
    return errors;
  };
  return <div className={uploaderWrapper}>
      <p className={preserUrlInstructions}>
        Enter public preview example URLs (max 5.) separated by a comma. These inputs will be available to try for you or the community.
      </p>
      <p className={contentWarning}>
        Do not upload any confidential or sensitive information. Use of this feature is for demonstration purposes only and is in a public
        environment.
      </p>
      <pre className={contentWarning}>
        Example: <code className={exampleUrls}>{exampleURLs}</code>
      </pre>
      <br />
      <Formik onSubmit={(values: {
      urls: string;
    }) => submitHandler(values)} initialValues={defaultValues} validate={validate}>
        {({
        isValid,
        dirty
      }) => {
        const confirmDisabled = !isValid || !dirty || isLoading;
        return <Form>
              <InputField isMultiLine name="urls" data-testid="presetinputs-textarea" className={cx(presetUrlTextArea, 'fullWidth')} />

              <Alert type="info">
                <div className={cx(flex.init, flex.alignItems.center)}>
                  <IconInfoFill className={cx(cssHelpers.margin.xl[0], cssHelpers.margin.xr[1], cssHelpers.margin.y[0])} color={baseTheme.light.colors.blue} size={18} />
                  <div>{`Note: The first URL will be set as model featured ${inputType}.`}</div>
                </div>
              </Alert>
              <ButtonGroup className={cx(flex.init, cssHelpers.margin.yb[1])}>
                <Button kind="secondary" className="fullWidth uppercase bold" id="signin-cancel" type="button" onClick={closeModal}>
                  Cancel
                </Button>
                <Button disabled={confirmDisabled} className="fullWidth uppercase bold" id="signin-submit" type="submit" data-testid="signInButton">
                  Confirm
                </Button>
              </ButtonGroup>
            </Form>;
      }}
      </Formik>
    </div>;
}
function filterValidUniqueUrls(commaSeparatedUrls: string): string[] {
  return uniq(commaSeparatedUrls.split(',')
  // remove spaces etc
  .map(x => x.trim())
  // check if URLs
  .filter(isUrl));
}